import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, IconButton,
  Chip, CircularProgress, Alert, Tabs, Tab, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Toolbar, Container, Stack,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { Check, X, Edit2, History, TrendingUp, PlusCircle } from "lucide-react";
import { tokens } from "../../theme";
import { alpha, useTheme } from "@mui/material/styles";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import CustomToast from "../../utils/functions/CustomToast";
import { USDollar } from "../../utils/functions/Helpers";
import Constants from "../../utils/constants/Constants";

const TAX_RATE_NAME = "Tax Rate";

const SUPPORTED_RATE_DEFINITIONS = [
  { name: Constants.SEAT_RATE_NAME, description: "Per-guest charge", step: "0.01" },
  { name: Constants.OVERTIME_HOURLY_RATE_NAME, description: "Per-hour overtime charge", step: "0.01" },
  { name: Constants.REGULAR_FACILITY_RATE_NAME, description: "Sun–Fri full-day flat rate", step: "0.01" },
  { name: Constants.SATURDAY_FACILITY_RATE_NAME, description: "Saturday full-day flat rate", step: "0.01" },
  { name: Constants.HOURLY_FACILITY_RATE_NAME, description: "Per-hour facility charge (hourly strategy)", step: "0.01" },
  { name: Constants.CLEANING_FEES_SMALL_GUESTS_COUNT_NAME, description: "Cleaning fee for ≤100 guests", step: "0.01" },
  { name: Constants.CLEANING_FEES_LARGE_GUESTS_COUNT_NAME, description: "Cleaning fee for >100 guests", step: "0.01" },
  { name: TAX_RATE_NAME, description: "Tax percentage applied to subtotal", step: "0.01" },
];

const percentageFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Admin Rate Configuration — Dedicated page for managing pricing rates.
 * Displays RATE-type AddOns in a clean table with inline editing.
 * Includes rate change audit history.
 */
const RateConfiguration = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rates, setRates] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingRateId, setPendingRateId] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedRateAudit, setSelectedRateAudit] = useState([]);
  const [selectedRateName, setSelectedRateName] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRateName, setNewRateName] = useState("");
  const [newRateValue, setNewRateValue] = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);

  const isDarkMode = theme.palette.mode === "dark";
  const primaryTextColor = colors.grey[100];
  const secondaryTextColor = isDarkMode ? colors.grey[300] : colors.grey[400];
  const surfaceTint = isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
  const actionSurface = isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
  const tableContentSx = {
    color: `${primaryTextColor} !important`,
    "& .MuiTableCell-root": {
      color: `${primaryTextColor} !important`,
    },
    "& .MuiTypography-root": {
      color: `${primaryTextColor} !important`,
    },
  };
  const tableCellBodySx = {
    color: `${primaryTextColor} !important`,
    borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
  };

  const normalizeQueryParams = (params = {}) => {
    const normalizedParams = { ...params };
    if (normalizedParams.pageSize != null && normalizedParams.size == null) {
      normalizedParams.size = normalizedParams.pageSize;
    }
    delete normalizedParams.pageSize;
    return normalizedParams;
  };

  const normalizeRate = (rate = {}) => ({
    ...rate,
    active: rate.active ?? rate.isActive ?? true,
    isActive: rate.isActive ?? rate.active ?? true,
  });

  const configuredRateNames = useMemo(
    () => new Set(rates.map((rate) => rate.name)),
    [rates]
  );

  const availableRateDefinitions = useMemo(
    () => SUPPORTED_RATE_DEFINITIONS.filter((rateDefinition) => !configuredRateNames.has(rateDefinition.name)),
    [configuredRateNames]
  );

  const selectedRateDefinition = useMemo(
    () => availableRateDefinitions.find((rateDefinition) => rateDefinition.name === newRateName) ?? null,
    [availableRateDefinitions, newRateName]
  );

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await HttpClient.getAxiosClient().get(
        `${ServerEndpoints.ADMIN_PRICING_ENDPOINT}`,
        { params: normalizeQueryParams({ pageSize: 50, page: 0 }) }
      );
      if (response.status === 200) {
        setRates((response.data.content || []).map(normalizeRate));
      }
    } catch (err) {
      console.error("Failed to load rates", err);
      CustomToast("Failed to load rates", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const response = await HttpClient.getAxiosClient().get(
        `${ServerEndpoints.ADMIN_PRICING_AUDIT_ENDPOINT}`,
        { params: { page: 0, size: 50 } }
      );
      if (response.status === 200) {
        setAuditLog(response.data.content || []);
      }
    } catch (err) {
      // Audit endpoint may not exist yet — fail silently
      console.warn("Audit log not available:", err.message);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    fetchAuditLog();
  }, [fetchRates, fetchAuditLog]);

  useEffect(() => {
    if (!createDialogOpen) {
      return;
    }

    if (availableRateDefinitions.length === 0) {
      setNewRateName("");
      return;
    }

    if (!availableRateDefinitions.some((rateDefinition) => rateDefinition.name === newRateName)) {
      setNewRateName(availableRateDefinitions[0].name);
    }
  }, [availableRateDefinitions, createDialogOpen, newRateName]);

  const handleEdit = (rate) => {
    setEditingId(rate.id);
    setEditValue(rate.price.toString());
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue("");
  };

  const buildPricingUpdatePayload = (rate, overrides = {}) => {
    const nextActiveState = overrides.active ?? overrides.isActive ?? rate.active ?? rate.isActive ?? true;

    return {
      ...rate,
      ...overrides,
      active: nextActiveState,
      isActive: nextActiveState,
    };
  };

  const buildPricingFormData = (payload) => {
    const formData = new FormData();
    formData.append("addOn", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    return formData;
  };

  const submitPricingUpdate = async (rate, overrides, successMessage) => {
    setSaving(true);
    setPendingRateId(rate.id);

    try {
      const updatedAddOn = buildPricingUpdatePayload(rate, overrides);
      await HttpClient.getAxiosClient().put(
        `${ServerEndpoints.ADMIN_PRICING_ENDPOINT}/${rate.id}`,
        buildPricingFormData(updatedAddOn),
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      CustomToast(successMessage, "success");
      fetchRates();
      fetchAuditLog();
    } catch (err) {
      console.error("Failed to update rate", err);
      CustomToast("Failed to update rate", "error");
    } finally {
      setSaving(false);
      setPendingRateId(null);
    }
  };

  const handleSave = async (rate) => {
    const newPrice = Number.parseFloat(editValue);
    if (Number.isNaN(newPrice) || newPrice < 0) {
      CustomToast("Please enter a valid price", "error");
      return;
    }

    if (newPrice === rate.price) {
      handleCancel();
      return;
    }

    await submitPricingUpdate(
      rate,
      { price: newPrice },
      `${rate.name} updated to ${formatRateValue(rate.name, newPrice)}`
    );
    handleCancel();
  };

  const handleToggleStatus = async (rate) => {
    const nextActiveState = !(rate.active ?? rate.isActive ?? true);
    await submitPricingUpdate(
      rate,
      { active: nextActiveState, isActive: nextActiveState },
      `${rate.name} ${nextActiveState ? "activated" : "deactivated"}.`
    );
  };

  const handleOpenCreateDialog = () => {
    if (availableRateDefinitions.length === 0) {
      CustomToast("All supported pricing entries are already configured.", "info");
      return;
    }

    setCreateError("");
    setNewRateValue("");
    setNewRateName(availableRateDefinitions[0].name);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (creating) {
      return;
    }

    setCreateDialogOpen(false);
    setCreateError("");
    setNewRateValue("");
  };

  const handleCreateRate = async () => {
    if (!selectedRateDefinition) {
      setCreateError("Select a supported pricing entry to create.");
      return;
    }

    const parsedValue = Number.parseFloat(newRateValue);
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      setCreateError("Enter a valid value greater than or equal to zero.");
      return;
    }

    setCreateError("");
    setCreating(true);

    try {
      await HttpClient.getAxiosClient().post(
        `${ServerEndpoints.ADMIN_PRICING_ENDPOINT}`,
        buildPricingFormData({
          name: selectedRateDefinition.name,
          category: Constants.CATEGORY_FACILITY,
          description: selectedRateDefinition.description,
          price: parsedValue,
          active: true,
          isActive: true,
          addonType: Constants.ADDON_TYPE.RATE,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      CustomToast(`${selectedRateDefinition.name} created successfully.`, "success");
      setCreateDialogOpen(false);
      setNewRateValue("");
      await fetchRates();
    } catch (err) {
      console.error("Failed to create rate", err);
      setCreateError(err?.response?.data?.message || "Failed to create pricing entry.");
    } finally {
      setCreating(false);
    }
  };

  const handleViewHistory = async (rate) => {
    setSelectedRateName(rate.name);
    setAuditDialogOpen(true);
    try {
      const response = await HttpClient.getAxiosClient().get(
        `${ServerEndpoints.ADMIN_PRICING_AUDIT_ENDPOINT}/${rate.id}`
      );
      if (response.status === 200) {
        setSelectedRateAudit(response.data || []);
      }
    } catch {
      setSelectedRateAudit([]);
    }
  };

  const formatRateValue = (name, price) => {
    const numericPrice = Number(price ?? 0);

    if (name === TAX_RATE_NAME) {
      return `${percentageFormatter.format(numericPrice)}%`;
    }

    return USDollar.format(numericPrice);
  };

  const tableCellHeaderSx = {
    fontWeight: 700,
    fontFamily: "Josefin Sans",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: colors.grey[100],
    borderBottom: `1px solid ${isDarkMode ? colors.primary[300] : colors.grey[200]}`,
  };

  const tableRowSx = {
    "&:hover": {
      backgroundColor: isDarkMode
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.015)",
    },
    "& .MuiTableCell-root": {
      color: `${primaryTextColor} !important`,
      borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
    },
    "& .MuiTypography-root": {
      color: `${primaryTextColor} !important`,
    },
  };

  const cardPaperSx = {
    borderRadius: "24px",
    overflow: "hidden",
    backgroundColor: isDarkMode ? colors.primary[400] : "#FFFFFF",
    border: `1px solid ${isDarkMode ? colors.primary[300] : colors.grey[200]}`,
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  };

  const getRateDescription = (name) => {
    return SUPPORTED_RATE_DEFINITIONS.find((rateDefinition) => rateDefinition.name === name)?.description || "";
  };

  const renderRateTableContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (rates.length === 0) {
      return (
        <Alert
          severity="info"
          sx={{
            m: 3,
            borderRadius: 3,
            color: primaryTextColor,
            bgcolor: isDarkMode ? alpha(colors.blueAccent[500], 0.14) : undefined,
            "& .MuiAlert-icon": { color: isDarkMode ? colors.blueAccent[300] : undefined },
          }}
        >
          No rate AddOns found. Rates are using hardcoded defaults.
        </Alert>
      );
    }

    return (
      <TableContainer sx={tableContentSx}>
        <Table sx={tableContentSx}>
          <TableHead>
            <TableRow sx={{ bgcolor: surfaceTint }}>
              <TableCell sx={tableCellHeaderSx}>Rate Name</TableCell>
              <TableCell sx={tableCellHeaderSx}>Description</TableCell>
              <TableCell sx={tableCellHeaderSx}>Current Value</TableCell>
              <TableCell sx={tableCellHeaderSx}>Status</TableCell>
              <TableCell sx={{ ...tableCellHeaderSx, textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map((rate) => (
              <TableRow key={rate.id} hover sx={tableRowSx}>
                <TableCell sx={tableCellBodySx}>
                  <Typography fontWeight={600} sx={{ color: `${primaryTextColor} !important` }}>{rate.name}</Typography>
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  <Typography variant="body2" sx={{ color: `${secondaryTextColor} !important` }}>
                    {getRateDescription(rate.name)}
                  </Typography>
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  {editingId === rate.id ? (
                    <TextField
                      size="small"
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                      sx={{
                        width: 140,
                        "& .MuiInputBase-root": {
                          color: primaryTextColor,
                          bgcolor: isDarkMode ? alpha(colors.primary[300], 0.18) : "#FFFFFF",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: isDarkMode ? alpha(colors.grey[100], 0.28) : colors.grey[300],
                        },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: isDarkMode ? alpha(colors.grey[100], 0.45) : colors.grey[500],
                        },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.greenAccent[500],
                        },
                        "& input": {
                          color: primaryTextColor,
                        },
                      }}
                      inputProps={{ step: rate.name === TAX_RATE_NAME ? "0.1" : "1", min: 0 }}
                    />
                  ) : (
                    <Typography fontWeight={600} fontSize="1.1rem" sx={{ color: `${primaryTextColor} !important` }}>
                      {formatRateValue(rate.name, rate.price)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(rate.active)}
                      disabled={saving && pendingRateId === rate.id}
                      onChange={() => handleToggleStatus(rate)}
                      inputProps={{ "aria-label": `Toggle ${rate.name} status` }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: colors.greenAccent[500],
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: colors.greenAccent[500],
                        },
                      }}
                    />
                    <Chip
                      label={rate.active ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: rate.active
                          ? alpha(colors.greenAccent[500], 0.18)
                          : alpha(colors.grey[400], 0.14),
                        color: rate.active ? colors.greenAccent[400] : secondaryTextColor,
                      }}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...tableCellBodySx, textAlign: "center" }}>
                  {editingId === rate.id ? (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleSave(rate)}
                        disabled={saving && pendingRateId === rate.id}
                        sx={{ borderRadius: "10px" }}
                      >
                        <Check size={18} />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={handleCancel} disabled={saving && pendingRateId === rate.id} sx={{ borderRadius: "10px" }}>
                        <X size={18} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(rate)}
                        sx={{
                          borderRadius: "10px",
                          bgcolor: actionSurface,
                          color: primaryTextColor,
                        }}
                        disabled={saving && pendingRateId === rate.id}
                      >
                        <Edit2 size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleViewHistory(rate)}
                        sx={{
                          borderRadius: "10px",
                          bgcolor: actionSurface,
                          color: primaryTextColor,
                        }}
                        disabled={saving && pendingRateId === rate.id}
                      >
                        <History size={16} />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAuditTableContent = () => {
    if (auditLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (auditLog.length === 0) {
      return (
        <Alert
          severity="info"
          sx={{
            m: 3,
            borderRadius: 3,
            color: primaryTextColor,
            bgcolor: isDarkMode ? alpha(colors.blueAccent[500], 0.14) : undefined,
            "& .MuiAlert-icon": { color: isDarkMode ? colors.blueAccent[300] : undefined },
          }}
        >
          No rate changes recorded yet.
        </Alert>
      );
    }

    return (
      <TableContainer sx={tableContentSx}>
        <Table sx={tableContentSx}>
          <TableHead>
            <TableRow sx={{ bgcolor: surfaceTint }}>
              <TableCell sx={tableCellHeaderSx}>Rate</TableCell>
              <TableCell sx={tableCellHeaderSx}>Old Value</TableCell>
              <TableCell sx={tableCellHeaderSx}>New Value</TableCell>
              <TableCell sx={tableCellHeaderSx}>Changed At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLog.map((entry) => (
              <TableRow key={entry.id} hover sx={tableRowSx}>
                <TableCell sx={tableCellBodySx}>{entry.addonName}</TableCell>
                <TableCell sx={tableCellBodySx}>
                  <Typography sx={{ color: `${theme.palette.error.main} !important` }}>
                    {formatRateValue(entry.addonName, entry.oldPrice)}
                  </Typography>
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  <Typography sx={{ color: `${theme.palette.success.main} !important` }}>
                    {formatRateValue(entry.addonName, entry.newPrice)}
                  </Typography>
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  {entry.changedAt
                    ? new Date(entry.changedAt).toLocaleString()
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Toolbar sx={{ minHeight: "70px !important" }} />
      <Container maxWidth="xl">
        <Box sx={{ mt: 2, mx: { xs: 0, md: 3 }, pb: 6 }}>
          <Box
            sx={{
              mb: 4,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              p: { xs: 3, md: 4 },
              borderRadius: "24px",
              border: `1px solid ${isDarkMode ? colors.primary[400] : colors.grey[200]}`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={3}
            >
              <Box>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}
                >
                  Pricing Configuration
                </Typography>
                <Typography
                  variant="h6"
                  color={isDarkMode ? colors.greenAccent[500] : colors.greenAccent[300]}
                  fontWeight="500"
                >
                  Manage facility rates, fees, and tax settings for new reservations.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<PlusCircle size={16} />}
                  onClick={handleOpenCreateDialog}
                  disabled={availableRateDefinitions.length === 0}
                  sx={{
                    height: 38,
                    px: 2,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: colors.greenAccent[500],
                    color: isDarkMode ? colors.primary[500] : colors.primary[100],
                    "&:hover": { bgcolor: colors.greenAccent[400] },
                    "&.Mui-disabled": {
                      bgcolor: isDarkMode ? alpha(colors.grey[500], 0.2) : colors.grey[200],
                      color: secondaryTextColor,
                    },
                  }}
                >
                  Add Pricing Entry
                </Button>
                <Chip
                  icon={<TrendingUp size={16} />}
                  label={`${rates.length} configured rates`}
                  sx={{
                    height: 38,
                    px: 1,
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.72)",
                    color: colors.grey[100],
                    borderRadius: "12px",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: colors.greenAccent[500] },
                  }}
                />
                <Chip
                  icon={<History size={16} />}
                  label={`${auditLog.length} recent changes`}
                  sx={{
                    height: 38,
                    px: 1,
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.72)",
                    color: colors.grey[100],
                    borderRadius: "12px",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: colors.greenAccent[500] },
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          <Paper elevation={0} sx={cardPaperSx}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                borderBottom: `1px solid ${isDarkMode ? colors.primary[300] : colors.grey[200]}`,
                bgcolor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
              }}
            >
              <Tabs
                value={tabIndex}
                onChange={(_, v) => setTabIndex(v)}
                sx={{
                  minHeight: 0,
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 999,
                    backgroundColor: colors.greenAccent[500],
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    color: secondaryTextColor,
                    mr: 1,
                  },
                  "& .Mui-selected": {
                    color: `${colors.greenAccent[500]} !important`,
                  },
                }}
              >
                <Tab label="Active Rates" icon={<TrendingUp size={16} />} iconPosition="start" />
                <Tab label="Change History" icon={<History size={16} />} iconPosition="start" />
              </Tabs>
            </Box>

            {tabIndex === 0 && renderRateTableContent()}
            {tabIndex === 1 && renderAuditTableContent()}
          </Paper>
        </Box>
      </Container>

        <Dialog
          open={createDialogOpen}
          onClose={handleCloseCreateDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "24px",
              backgroundColor: isDarkMode ? colors.primary[400] : "#FFFFFF",
              border: `1px solid ${isDarkMode ? colors.primary[300] : colors.grey[200]}`,
              color: primaryTextColor,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: primaryTextColor }}>
            Create Pricing Entry
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                Only supported system pricing entries can be created here, so any new entry will actually participate in reservation pricing.
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="pricing-rate-name-label">Pricing Entry</InputLabel>
                <Select
                  labelId="pricing-rate-name-label"
                  value={newRateName}
                  label="Pricing Entry"
                  onChange={(event) => setNewRateName(event.target.value)}
                >
                  {availableRateDefinitions.map((rateDefinition) => (
                    <MenuItem key={rateDefinition.name} value={rateDefinition.name}>
                      {rateDefinition.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={selectedRateDefinition?.name === TAX_RATE_NAME ? "Value (%)" : "Value ($)"}
                type="number"
                value={newRateValue}
                onChange={(event) => setNewRateValue(event.target.value)}
                inputProps={{ step: selectedRateDefinition?.step ?? "0.01", min: 0 }}
                helperText={createError || selectedRateDefinition?.description || " "}
                error={Boolean(createError)}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} disabled={creating} sx={{ color: secondaryTextColor }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateRate}
              variant="contained"
              disabled={creating || !selectedRateDefinition}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                bgcolor: colors.greenAccent[500],
                color: isDarkMode ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400] },
              }}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Per-Rate History Dialog */}
        <Dialog
          open={auditDialogOpen}
          onClose={() => setAuditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "24px",
              backgroundColor: isDarkMode ? colors.primary[400] : "#FFFFFF",
              border: `1px solid ${isDarkMode ? colors.primary[300] : colors.grey[200]}`,
              color: primaryTextColor,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontFamily: "Josefin Sans", color: primaryTextColor }}>
            Price History — {selectedRateName}
          </DialogTitle>
          <DialogContent>
            {selectedRateAudit.length === 0 ? (
              <Typography color={secondaryTextColor} sx={{ py: 2 }}>
                No price changes recorded for this rate.
              </Typography>
            ) : (
              <Table size="small" sx={tableContentSx}>
                <TableHead>
                  <TableRow sx={{ bgcolor: surfaceTint }}>
                    <TableCell sx={tableCellHeaderSx}>Old</TableCell>
                    <TableCell sx={tableCellHeaderSx}>New</TableCell>
                    <TableCell sx={tableCellHeaderSx}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedRateAudit.map((entry) => (
                    <TableRow key={entry.id} sx={tableRowSx}>
                      <TableCell sx={tableCellBodySx}>{formatRateValue(selectedRateName, entry.oldPrice)}</TableCell>
                      <TableCell sx={tableCellBodySx}>{formatRateValue(selectedRateName, entry.newPrice)}</TableCell>
                      <TableCell sx={tableCellBodySx}>{new Date(entry.changedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAuditDialogOpen(false)} sx={{ color: isDarkMode ? colors.greenAccent[400] : colors.greenAccent[500] }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
};

export default RateConfiguration;
